import { Box, Grid, Typography, TextField, Button, FormHelperText, useMediaQuery, useTheme, MenuItem } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config.json';
import { Country, State } from 'country-state-city';
import { useUser } from "../../pages/login/UserContextPersistent";
import CircularProgress from '@mui/material/CircularProgress';
export default function Register() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { dispatch } = useUser();
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [registerData, setRegisterData] = useState({
    organizationName: "",
    organizationWebsite: "",
    country: "",
    state: "",
    contactName: "",
    address: "",
    email: "",
    phoneNumber: "",
    organizationStatus: "INACTIVE",
    city: "",
    pincode: "",
  });

  // Add state for country and state dropdown options
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Load countries on component mount
  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  // Update states when country changes
  useEffect(() => {
    if (selectedCountry) {
      setStates(State.getStatesOfCountry(selectedCountry.isoCode));
    } else {
      setStates([]);
    }
    // Clear the selected state when country changes
    setRegisterData(prev => ({
      ...prev,
      state: ''
    }));
  }, [selectedCountry]);

  const [errors, setErrors] = useState({
    organizationName: "",
    organizationWebsite: "",
    country: "",
    state: "",
    contactName: "",
    address: "",
    email: "",
    phoneNumber: "",
    city: "",
    pincode: "",
  });

  const [touched, setTouched] = useState({
    organizationName: false,
    organizationWebsite: false,
    country: false,
    state: false,
    contactName: false,
    address: false,
    email: false,
    phoneNumber: false,
    city: false,
    pincode: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for country selection
    if (name === 'country') {
      const countryObj = countries.find(country => country.name === value);
      setSelectedCountry(countryObj);
    }

    setRegisterData(prevData => ({
      ...prevData,
      [name]: value,
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });
    validateField(name, registerData[name]);
  };

  const validateField = (name, value) => {
    let errorMessage = "";

    switch (name) {
      case 'organizationName':
        errorMessage = value ? '' : 'Organization Name is required';
        break;
      case "country":
        errorMessage = value ? "" : "Country is required";
        break;
      case "state":
        errorMessage = value ? "" : "State is required";
        break;
      case "contactName":
        errorMessage = value ? "" : "Contact Name is required";
        break;
      case 'email':
        if (!value) {
          errorMessage = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          errorMessage = "Email is invalid";
        }
        break;
      case "phoneNumber":
        if (!value) {
          errorMessage = "Phone Number is required";
        } else if (!/^\d{10}$/.test(value.replace(/\D/g, ""))) {
          errorMessage = "Phone Number should be 10 digits";
        }
        break;
      case "city":
        errorMessage = value ? "" : "City is required";
        break;
      case "pincode":
        if (!value) {
          errorMessage = "Pincode is required";
        } else if (!/^\d{5,6}$/.test(value)) {
          errorMessage = "Pincode should be 5-6 digits";
        }
        break;
      case "organizationWebsite":
        errorMessage = value ? '' : 'Organization Website is required';
        break;
      case "address":
        errorMessage = value ? '' : 'Address is required';
        break;
      default:
        break;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: errorMessage,
    }));

    return !errorMessage;
  };

  const validateForm = () => {
    let isValid = true;
    let newErrors = {};
    let newTouched = {};

    // Get all fields to validate
    const fieldsToValidate = [
      'organizationName',
      'organizationWebsite',
      'country',
      'state',
      'contactName',
      'address',
      'email',
      'phoneNumber',
      'city',
      'pincode'
    ];
    fieldsToValidate.forEach((field) => {
      newTouched[field] = true;
      const fieldIsValid = validateField(field, registerData[field]);

      if (!fieldIsValid) {
        isValid = false;
        newErrors[field] = errors[field] || `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      }
    });

    setTouched(newTouched);
    // Update errors state
    setErrors(prevErrors => ({
      ...prevErrors,
      ...newErrors
    }));
    console.log(newErrors);
    return isValid;
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submission
    if (!validateForm()) {
      return;
    }

    setErrorMessage("");
    setIsLoading(true);
    try {
      const dataToSubmit = {
        name: registerData.organizationName,
        domain: registerData.organizationWebsite,
        address: registerData.address,
        city: registerData.city,
        country: registerData.country,
        pincode: registerData.pincode,
        contactName: registerData.contactName,
        contactEmail: registerData.email,
        contactPhone: registerData.phoneNumber,
        state: registerData.state,
        organizationStatus: "INACTIVE",
      };

      const response = await axios.post(`${config.baseURL}/register`, dataToSubmit, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      if (response.status === 201) {
        const { adminUser, organization, orgAdmin, registerOrganization } = response.data;

        // Store organization ID in localStorage for RegisterFeatures
        localStorage.setItem("registerOrganizationId", registerOrganization.id);

        dispatch({
          type: "SET_USER",
          payload: {
            id: adminUser.id,
            name: adminUser.name,
            userName: adminUser.userName,
            email: adminUser.email,
            organizationId: adminUser.organizationId,
            role: adminUser.roleId,
            domain: organization.domain,
            canAlterAccessRole: orgAdmin.canAlterAccessRole,
          },
        });

        dispatch({
          type: "SET_FEATURES",
          payload: [],
        });

        setRegisterData({
          organizationName: "",
          organizationWebsite: "",
          address: "",
          city: "",
          country: "",
          pincode: "",
          contactName: "",
          email: "",
          phoneNumber: "",
          organizationStatus: "",
          state: "",
        });

        setSuccessMessage("Registration successful");
        navigate("/registerConfirm");
      }
    } catch (error) {
      console.error("Error submitting form:", error.response?.data || error.message);
      setErrorMessage("An error occurred during registration. Please try again.");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const customTextFieldStyles = {
    '& .MuiOutlinedInput-root': {
      height: '55px',
      borderRadius: '16px',
      '& fieldset': {
        border: 'none',
      },
    },

    '& .MuiInputBase-root': {
      border: '2px solid #183059',
      borderRadius: '16px',
      height: '55px',
      padding: '16px 24px',
    },

    '& .MuiInputBase-root.Mui-error': {
      border: '2px solid #d32f2f',
    },

    '& .MuiInputLabel-root': {
      position: 'relative',
      transform: 'none',
      marginBottom: '8px',
    },
    marginBottom: "4px",
  };

  return (
    <Box
      sx={{
        padding: { xs: "16px", sm: "20px", md: "40px" },
        backgroundColor: "#EEEEEE",
      }}
    >
      <Box
        sx={{
          maxWidth: "1360px",
          borderRadius: "20px",
          padding: {
            xs: "20px",
            sm: "40px",
            md: "60px",
            lg: "80px 180px",
          },
          gap: { xs: "32px", md: "64px" },
          background: "#FFFFFF",
          border: "1px solid #A5DCF3",
          boxShadow: "-2px 2px 5px 0px #00000040",
          margin: "0 auto",
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography
              variant="h4"
              align="center"
              sx={{
                marginBottom: { xs: "16px", sm: "24px", md: "32px" },
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "24px", sm: "28px", md: "36px" },
                lineHeight: "141%",
                letterSpacing: "0%",
                verticalAlign: "middle",
                color: "#183059",
                padding: "10px",
                borderRadius: "8px",
              }}
            >
              Register your Organization & Get Started
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <form onSubmit={handleSubmit}>
              {/* Form Fields */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="organizationName"
                    label="Organization Name"
                    fullWidth
                    value={registerData.organizationName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.organizationName && !!errors.organizationName
                    }
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                  {touched.organizationName && errors.organizationName && (
                    <FormHelperText error>
                      {errors.organizationName}
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="organizationWebsite"
                    label="Organization Website"
                    fullWidth
                    error={
                      touched.organizationWebsite && !!errors.organizationWebsite
                    }
                    value={registerData.organizationWebsite}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                  {touched.organizationWebsite && errors.organizationWebsite && (
                    <FormHelperText error>
                      {errors.organizationWebsite}
                    </FormHelperText>
                  )}
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mt: { xs: 0 } }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    name="country"
                    label="Country"
                    fullWidth
                    value={registerData.country}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.country && !!errors.country}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={{
                      ...customTextFieldStyles,
                      '& .MuiInputBase-root': {
                        ...customTextFieldStyles['& .MuiInputBase-root'],
                      }
                    }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 250,
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Country</em>
                    </MenuItem>
                    {countries.map((country) => (
                      <MenuItem key={country.isoCode} value={country.name}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  {touched.country && errors.country && (
                    <FormHelperText error>{errors.country}</FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    name="state"
                    label="State"
                    fullWidth
                    value={registerData.state}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.state && !!errors.state}
                    disabled={!selectedCountry}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={{
                      ...customTextFieldStyles,
                      '& .MuiInputBase-root': {
                        ...customTextFieldStyles['& .MuiInputBase-root'],
                      }
                    }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 250,
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select State</em>
                    </MenuItem>
                    {states.map((state) => (
                      <MenuItem key={state.isoCode} value={state.name}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  {touched.state && errors.state && (
                    <FormHelperText error>{errors.state}</FormHelperText>
                  )}
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mt: { xs: 0 } }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="contactName"
                    label="Contact Name"
                    fullWidth
                    value={registerData.contactName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.contactName && !!errors.contactName}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                  {touched.contactName && errors.contactName && (
                    <FormHelperText error>{errors.contactName}</FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="address"
                    label="Address"
                    fullWidth
                    value={registerData.address}
                    error={touched.address && !!errors.address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                  {touched.address && errors.address && (
                    <FormHelperText error>
                      {errors.address}
                    </FormHelperText>
                  )}
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mt: { xs: 0 } }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="email"
                    label="Email"
                    fullWidth
                    type="email"
                    value={registerData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && !!errors.email}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                  {touched.email && errors.email && (
                    <FormHelperText error>{errors.email}</FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="phoneNumber"
                    label="Phone Number"
                    fullWidth
                    value={registerData.phoneNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.phoneNumber && !!errors.phoneNumber}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                  {touched.phoneNumber && errors.phoneNumber && (
                    <FormHelperText error>{errors.phoneNumber}</FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="city"
                    label="City"
                    fullWidth
                    value={registerData.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.city && !!errors.city}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                  {touched.city && errors.city && (
                    <FormHelperText error>{errors.city}</FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    name="pincode"
                    label="Pincode"
                    fullWidth
                    value={registerData.pincode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.pincode && !!errors.pincode}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                  {touched.pincode && errors.pincode && (
                    <FormHelperText error>{errors.pincode}</FormHelperText>
                  )}
                </Grid>
              </Grid>

              {/* Buttons */}
              <Grid container spacing={3} sx={{ mt: { xs: 1, sm: 2 } }}>
                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="center"
                  gap={2}
                  sx={{
                    flexDirection: isMobileScreen ? "column" : "row",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      minWidth: "120px",
                      width: isMobileScreen ? "100%" : "200px",
                      height: "55px",
                      borderRadius: "8px",
                      borderWidth: "1px",
                      padding: "16px 24px",
                      background: "#F2F2F3",
                      border: "1px solid #414958",
                      color: "#1F242E",
                      textTransform: "none",
                      fontFamily: "Roboto",
                      fontWeight: "600",
                      fontSize: "20px",

                      mb: isMobileScreen ? 1 : 0,
                    }}
                    onClick={() => navigate("/")}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={isLoading}
                    sx={{
                      minWidth: "120px",
                      width: isMobileScreen ? "100%" : "200px",
                      height: "55px",
                      borderRadius: "8px",
                      borderWidth: "1px",
                      padding: "16px 24px",
                      background: isLoading ? "#CCCCCC" : "#F9E719",
                      border: "1px solid #414958",
                      color: "#1F242E",
                      textTransform: "none",
                      fontFamily: "Roboto",
                      fontWeight: "600",
                      fontSize: "20px",
                      ":hover": {
                        background: isLoading ? "#CCCCCC" : "#E2D216",
                      }
                    }}
                    onClick={handleSubmit}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} sx={{ color: "#1F242E" }} />
                    ) : (
                      "Register"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}